import { MarketingCampaigns, MarketingMediumTemplates } from '@prisma/client';
import { AxiosError } from 'axios';
import API_URL from '../apiconfig';
import {
  CampaignSegment,
  CreateTemplateAndEmbedResponse,
  FinalCampaignSegment,
  GetMarketingDataParams,
  MarketingBusinessesWithCampaigns,
  MarketingCampaignSegmentWithFilters,
  MarketingEmailTemplateWithSource,
  MarketingSmsTemplateWithSource,
  PocketflowsTemplateEmbed,
  SegmentCustomer,
  CreateUpdateCampaignPayload,
  UpsertCampaignPayload,
  SmsContentSchema,
  EmailContentSchema,
  CreateUpdateDraftCampaignPayload,
  PocketflowsProjectEmailTemplate,
  PocketflowsCampaign,
  MarketingCampaignWithSource,
  CampaignCost,
  PocketflowsCustomer,
} from '../types/marketing';
import axios from './api';
import client from '../utils/react-query/client';

export async function getMarketingData(params: GetMarketingDataParams) {
  try {
    const marketingData = await axios.get(`${API_URL}/v1/marketing`, {
      params,
    });

    return marketingData.data.result as MarketingBusinessesWithCampaigns;
  } catch (error) {
    throw new Error('Cannot get employee');
  }
}

export async function upsertCampaign(
  marketingBusinessId: string,
  payload: CreateUpdateCampaignPayload | CreateUpdateDraftCampaignPayload,
  draft?: boolean,
) {
  try {
    const templateId =
      payload.medium === 'SMS'
        ? (payload?.content as SmsContentSchema)?.smsTemplateId
        : payload.medium === 'EMAIL'
        ? (payload?.content as EmailContentSchema)?.emailTemplateId
        : null;
    if (!templateId && !draft) throw new Error('Invalid template');

    // Prebuilt templates have an string id, custom templates have an integer id
    const includeSegmentId = Number.isInteger(payload.includeSegment?.id)
      ? (payload.includeSegment?.id as number)
      : null;
    const excludeSegmentId = Number.isInteger(payload.excludeSegment?.id)
      ? (payload.excludeSegment?.id as number)
      : null;
    const prebuiltIncludeSegmentId = !Number.isInteger(
      payload.includeSegment?.id,
    )
      ? (payload.includeSegment?.id as string)
      : null;
    const prebuiltExcludeSegmentId = !Number.isInteger(
      payload.excludeSegment?.id,
    )
      ? (payload.excludeSegment?.id as string)
      : null;

    const sendTime =
      draft && payload.sendTime && !Object.keys(payload.sendTime).length
        ? null
        : payload.sendTime;

    const body: UpsertCampaignPayload = {
      id: payload.id,
      name: payload.name,
      medium: payload.medium,
      sendTime,
      templateId,
      includeSegmentId,
      prebuiltIncludeSegmentId,
      includeSegment: payload.includeSegment,
      excludeSegmentId,
      prebuiltExcludeSegmentId,
      excludeSegment: payload.excludeSegment,
      isFinal: !draft,
      emailAddressId: payload.emailAddressId,
    };

    const campaign = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/campaign`,
      body,
    );

    return {
      campaign: campaign.data.result?.campaign as MarketingCampaignWithSource,
    };
  } catch (error) {
    return {
      error:
        // @ts-expect-error
        (error as AxiosError).response?.data?.message ||
        'Cannot create campaign',
    };
  }
}

export async function deleteCampaign(
  marketingBusinessId: string,
  campaignId: string,
) {
  try {
    await axios.delete(
      `${API_URL}/v1/marketing/${marketingBusinessId}/campaign/${campaignId}`,
    );
  } catch (error) {
    throw new Error('Cannot delete campaign');
  }
}

export async function stopCampaign(
  marketingBusinessId: string,
  campaignId: string,
) {
  try {
    const result = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/campaign/${campaignId}/stop`,
    );

    return result.data.result as MarketingCampaigns;
  } catch (error) {
    throw new Error('Cannot stop campaign');
  }
}

export async function getSegmentCustomers(
  marketingBusinessId: string,
  data: CampaignSegment,
) {
  try {
    const customers = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/segment-customers`,
      { ...data, take: 50 },
    );

    return customers.data.result as {
      totalCount: number;
      customers: SegmentCustomer[];
      sourceCustomers: PocketflowsCustomer[];
    };
  } catch (error) {
    throw new Error('Cannot get segment customers');
  }
}

export async function getCombinedSegmentCustomers(
  marketingBusinessId: string,
  data: {
    includeSegment: CampaignSegment | null;
    excludeSegment: CampaignSegment | null;
  },
) {
  try {
    const customers = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/segment-customers/combined`,
      data,
    );

    return customers.data.result as {
      totalCount: number;
    };
  } catch (error) {
    throw new Error('Cannot get segment customers');
  }
}

export async function getSegments(marketingBusinessId: string) {
  try {
    const segments = await axios.get(
      `${API_URL}/v1/marketing/${marketingBusinessId}/segments`,
    );

    return segments.data.result as MarketingCampaignSegmentWithFilters[];
  } catch (error) {
    throw new Error('Cannot get segments');
  }
}

export async function createSegment(
  marketingBusinessId: string,
  data: FinalCampaignSegment,
) {
  try {
    const segment = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/segments`,
      data,
    );

    return segment.data.result as MarketingCampaignSegmentWithFilters;
  } catch (error) {
    throw new Error('Cannot create segment');
  }
}

export async function invalidateSegments() {
  await client.invalidateQueries({
    queryKey: ['marketing-segments'],
    exact: false,
  });
}

export async function invalidateEmailTemplates() {
  await client.invalidateQueries({
    queryKey: ['marketing-email-templates'],
    exact: false,
  });
}

export async function invalidateTemplates() {
  await client.invalidateQueries({
    queryKey: ['marketing-sms-templates'],
    exact: false,
  });
  await invalidateEmailTemplates();
}

export async function getBusinessEmailTemplates(marketingBusinessId: string) {
  try {
    const templates = await axios.get(
      `${API_URL}/v1/marketing/${marketingBusinessId}/email-templates`,
    );

    return templates.data.result as {
      projectTemplates: PocketflowsProjectEmailTemplate[];
      templates: MarketingEmailTemplateWithSource[];
    };
  } catch (error) {
    throw new Error('Cannot get business templates');
  }
}

export async function getBusinessSmsTemplates(marketingBusinessId: string) {
  try {
    const templates = await axios.get(
      `${API_URL}/v1/marketing/${marketingBusinessId}/sms-templates`,
    );

    return templates.data.result as {
      templates: MarketingSmsTemplateWithSource[];
    };
  } catch (error) {
    throw new Error('Cannot get business templates');
  }
}

export async function createEmailTemplateAndEmbed(marketingBusinessId: string) {
  try {
    const embed = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/email-templates/embeds`,
    );

    return embed.data.result as CreateTemplateAndEmbedResponse;
  } catch (error) {
    throw new Error('Cannot create email template and embed');
  }
}

export async function createSmsTemplateAndEmbed(marketingBusinessId: string) {
  try {
    const embed = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/sms-templates/embeds`,
    );

    return embed.data.result as CreateTemplateAndEmbedResponse;
  } catch (error) {
    throw new Error('Cannot create SMS template and embed');
  }
}

export async function createEmailEmbed(
  marketingBusinessId: string,
  emailTemplateId: number,
) {
  try {
    const embed = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/email-templates/${emailTemplateId}/embeds`,
    );

    return embed.data.result as PocketflowsTemplateEmbed;
  } catch (error) {
    throw new Error('Cannot create email embed');
  }
}

export async function createSmsEmbed(
  marketingBusinessId: string,
  smsTemplateId: number,
) {
  try {
    const embed = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/sms-templates/${smsTemplateId}/embeds`,
    );

    return embed.data.result as PocketflowsTemplateEmbed;
  } catch (error) {
    throw new Error('Cannot create sms embed');
  }
}

export async function deleteEmailTemplate(
  marketingBusinessId: string,
  emailTemplateId: number,
) {
  try {
    await axios.delete(
      `${API_URL}/v1/marketing/${marketingBusinessId}/email-templates/${emailTemplateId}`,
    );

    return null;
  } catch (error) {
    throw new Error('Cannot delete email template');
  }
}

export async function deleteSmsTemplate(
  marketingBusinessId: string,
  smsTemplateId: number,
) {
  try {
    await axios.delete(
      `${API_URL}/v1/marketing/${marketingBusinessId}/sms-templates/${smsTemplateId}`,
    );

    return null;
  } catch (error) {
    throw new Error('Cannot delete sms template');
  }
}

export async function updateEmailTemplate(
  marketingBusinessId: string,
  emailTemplateId: number,
) {
  try {
    const template = await axios.patch(
      `${API_URL}/v1/marketing/${marketingBusinessId}/email-templates/${emailTemplateId}`,
    );

    return template.data.result as MarketingMediumTemplates;
  } catch (error) {
    throw new Error('Cannot update email template');
  }
}

export async function updateSmsTemplate(
  marketingBusinessId: string,
  smsTemplateId: number,
) {
  try {
    const template = await axios.patch(
      `${API_URL}/v1/marketing/${marketingBusinessId}/sms-templates/${smsTemplateId}`,
    );

    return template.data.result as MarketingMediumTemplates;
  } catch (error) {
    throw new Error('Cannot update sms template');
  }
}

export async function copyProjectEmailTemplate(
  marketingBusinessId: string,
  sourceProjectTemplateId: string,
) {
  try {
    const template = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/project-email-templates`,
      { sourceTemplateId: sourceProjectTemplateId },
    );

    return template.data.result as CreateTemplateAndEmbedResponse;
  } catch (error) {
    throw new Error('Cannot copy project email template');
  }
}

export async function finalizeCampaign({
  businessId,
  marketingBusinessId,
  campaignId,
}: {
  businessId: string;
  marketingBusinessId: string;
  campaignId: string;
}) {
  try {
    const campaign = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/campaign/${campaignId}/finalize`,
      {
        businessId,
      },
    );

    return {
      sourceCampaign: campaign.data.result as PocketflowsCampaign,
    };
  } catch (error) {
    return {
      error:
        // @ts-expect-error
        (error as AxiosError).response?.data?.message ||
        'Cannot finalize campaign',
    };
  }
}

export async function getCampaignCost({
  marketingBusinessId,
  marketingCampaignId,
}: {
  marketingBusinessId: string;
  marketingCampaignId: string;
}) {
  try {
    const usage = await axios.get(
      `${API_URL}/v1/marketing/${marketingBusinessId}/campaign/${marketingCampaignId}/cost`,
    );

    return usage.data.result as CampaignCost;
  } catch (error) {
    throw new Error('Cannot get segment usage');
  }
}
